import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import type { FauteJeu, OrigineJeu, RapportJeu } from "./contract";
import { LIBELLE_ORIGINE } from "./livres-jeux-textes";
import { statutDe } from "./queries";
import { EcranDErreur, ENCADRE_PANNE } from "./states";

/**
 * Ce que les ecrans du module Livres-jeux partagent : le fil d'Ariane, le livre ou la scene
 * introuvable, le badge d'origine d'un texte, les encadres, le rapport du verificateur et le
 * telechargement d'un fichier. Rien n'y vient d'un autre module.
 */

/** Les liens poses comme des boutons (`Button` du template ne rend qu'un `button`). */
export const LIEN_BOUTON =
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-left text-sm font-medium transition hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
export const LIEN_BOUTON_PLEIN =
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-left text-sm font-medium text-primary-foreground transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
/** Un bouton en contour qui garde une `ref` (celui du template ne la transmet pas). */
export const BOUTON_CONTOUR = cn(LIEN_BOUTON, "disabled:pointer-events-none disabled:opacity-50");
export const LIEN_TEXTE =
  "font-medium underline decoration-muted-foreground/60 underline-offset-[3px] hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm";

export const ALERTE_GRISE = "rounded-md border border-border bg-muted px-3.5 py-2.5 text-sm";
export const ALERTE_JAUNE =
  "rounded-md border border-avertissement/60 bg-avertissement/15 px-3.5 py-2.5 text-sm";
export const ALERTE_PANNE = ENCADRE_PANNE;
export const ALERTE_VERTE =
  "rounded-md border border-provenance-ecrit/40 bg-provenance-ecrit/10 px-3.5 py-2.5 text-sm";

export const CHAMP =
  "w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";
/** Le YAML des choix : chasse fixe, et 16 px au telephone (en dessous, iOS agrandit la page). */
export const CHAMP_YAML = cn(CHAMP, "min-h-48 font-mono text-base leading-relaxed md:text-sm");

/** Une carte du module, titre et contenu. */
export function Carte({
  titre,
  id,
  children,
  className,
}: {
  titre: ReactNode;
  id?: string | undefined;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <section
      aria-labelledby={id}
      className={cn(
        "flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4 text-card-foreground md:p-5",
        className,
      )}
    >
      <h2 className="text-base font-semibold" id={id}>
        {titre}
      </h2>
      {children}
    </section>
  );
}

/**
 * Le fil d'Ariane : `Livres-jeux › livre › scene` sur grand ecran ; au telephone, un seul lien
 * vers le parent, precede de « ‹ ».
 */
export function FilLivresJeux({
  livre,
  children,
}: {
  livre?: { readonly id: number; readonly titre: string } | undefined;
  children?: ReactNode;
}) {
  const separateur = (
    <span aria-hidden="true" className="max-md:hidden">
      {" › "}
    </span>
  );
  return (
    <nav aria-label="Fil d'Ariane" className="text-sm text-muted-foreground">
      <span aria-hidden="true" className="md:hidden">
        {"‹ "}
      </span>
      <Link
        className={cn("underline-offset-2 hover:underline", livre !== undefined && "max-md:hidden")}
        to="/livres-jeux"
      >
        Livres-jeux
      </Link>
      {livre === undefined ? null : (
        <>
          {separateur}
          <Link
            className="underline-offset-2 hover:underline"
            params={{ id: String(livre.id) }}
            search={{ onglet: "scenes" }}
            to="/livres-jeux/livres/$id"
          >
            {livre.titre}
          </Link>
        </>
      )}
      {children === undefined || children === null ? null : (
        <span className="max-md:hidden">
          {separateur}
          {children}
        </span>
      )}
    </nav>
  );
}

export function IntrouvableLivresJeux({ quoi }: { quoi: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Rien sous ce nom</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm text-muted-foreground">
        <p>Les livres-jeux ne connaissent pas {quoi}.</p>
        <p>
          <Link className="underline underline-offset-2" to="/livres-jeux">
            Revenir aux livres
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}

/** Les erreurs de lecture : 404 a la facon du module, le reste par `EcranDErreur`. */
export function ErreurLivresJeux({ error, quoi }: { error: unknown; quoi: string }) {
  if (statutDe(error) === 404) return <IntrouvableLivresJeux quoi={quoi} />;
  return <EcranDErreur error={error} quoi={quoi} />;
}

const TON_ORIGINE: Readonly<Record<OrigineJeu, string>> = {
  nicolas: "bg-provenance-ecrit/15 text-provenance-ecrit",
  claude: "bg-provenance-genere/15 text-provenance-genere",
  "les-deux": "bg-provenance-assiste/15 text-provenance-assiste",
};

/** Qui a ecrit le texte : Nicolas, Claude, ou les deux ; rien sur un texte vide. */
export function BadgeOrigine({ origine }: { origine: OrigineJeu | null }) {
  if (origine === null) {
    return (
      <span className="inline-flex rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
        vide
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium",
        TON_ORIGINE[origine],
      )}
    >
      {LIBELLE_ORIGINE[origine]}
    </span>
  );
}

/** Une faute du moteur : son message, et un lien vers sa scene quand elle en nomme une. */
function Faute({ faute }: { faute: FauteJeu }) {
  return (
    <li className="[overflow-wrap:anywhere]">
      {faute.scene_id === null ? (
        faute.message
      ) : (
        <>
          <Link
            className={LIEN_TEXTE}
            params={{ id: String(faute.scene_id) }}
            to="/livres-jeux/scenes/$id"
          >
            {faute.scene_cle}
          </Link>
          {" : "}
          {faute.message}
        </>
      )}
    </li>
  );
}

/** Le rapport du verificateur : fautes, avertissements, et ce que le moteur a explore. */
export function Rapport({ rapport }: { rapport: RapportJeu }) {
  return (
    <div className="flex flex-col gap-3 text-sm" role="status">
      <p className={rapport.ok ? ALERTE_VERTE : ALERTE_PANNE}>
        {rapport.ok
          ? "Le livre passe la vérification : chaque scène est atteignable, chaque partie finit."
          : `Le livre a ${rapport.erreurs.length > 1 ? `${rapport.erreurs.length} fautes` : "une faute"} à corriger.`}
      </p>
      {rapport.erreurs.length === 0 ? null : (
        <div>
          <h3 className="mb-1 font-semibold">Fautes</h3>
          <ul className="flex list-disc flex-col gap-1 pl-5">
            {rapport.erreurs.map((faute, rang) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: le rapport n'a pas d'identifiants
              <Faute faute={faute} key={rang} />
            ))}
          </ul>
        </div>
      )}
      {rapport.avertissements.length === 0 ? null : (
        <div>
          <h3 className="mb-1 font-semibold">Avertissements</h3>
          <ul className="flex list-disc flex-col gap-1 pl-5 text-muted-foreground">
            {rapport.avertissements.map((faute, rang) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: le rapport n'a pas d'identifiants
              <Faute faute={faute} key={rang} />
            ))}
          </ul>
        </div>
      )}
      {rapport.stats === null ? null : (
        <p className="text-muted-foreground">
          {rapport.stats.scenes} scènes, {rapport.stats.choix} choix, {rapport.stats.etats} états
          explorés.{" "}
          {Object.keys(rapport.stats.fins).length === 0
            ? "Aucune fin atteinte."
            : `Fins atteintes : ${Object.entries(rapport.stats.fins)
                .map(([fin, nombre]) => `${fin} (${nombre})`)
                .join(", ")}.`}
          {Object.keys(rapport.stats.plus_court).length === 0
            ? null
            : ` Chemin le plus court : ${Object.entries(rapport.stats.plus_court)
                .map(([fin, pas]) => `${fin} en ${pas} pas`)
                .join(", ")}.`}
        </p>
      )}
    </div>
  );
}

/** Fait telecharger des octets sous un nom de fichier, sans rien garder. */
export function telecharger(contenu: BlobPart, type: string, nom: string): void {
  const adresse = URL.createObjectURL(new Blob([contenu], { type }));
  const lien = document.createElement("a");
  lien.href = adresse;
  lien.download = nom;
  document.body.append(lien);
  lien.click();
  lien.remove();
  // Safari sur iPhone lit l'adresse apres le clic, parfois bien apres : elle reste une minute.
  setTimeout(() => URL.revokeObjectURL(adresse), 60_000);
}
